import { AppConfigAPI } from '@models/types/appconfig.d';
import { AppConfigProvider } from '@root/app/app.config.provider';
import { IgnoreError } from './../../models/ignore-error.d';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EditTrackingChanges, EditTrackingChangesKeys } from '@models/edit-tracking-changes';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';
import { backoff } from '@shared/helpers/backoff';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ResultsetItem } from '@models/resultset-item';

@Injectable({ providedIn: 'root' })
export class TrackingService {
  private environment: AppConfigAPI;
  private _modelDataHasChanges$: BehaviorSubject<EditTrackingChanges> = new BehaviorSubject<EditTrackingChanges>({
    lookupTables: false,
    exogenousLines: false,
    liquiditySources: false,
    resultsets: false,
    auxiliaryEquations: false,
    balanceSheet: false,
  });
  private userTrackingDeleteUrl; // Get deleted user from tracking

  public get modelDataHasChanges$(): BehaviorSubject<EditTrackingChanges> {
    return this._modelDataHasChanges$;
  }
  public set modelDataHasChanges$(value: BehaviorSubject<EditTrackingChanges>) {
    this._modelDataHasChanges$ = value;
  }

  constructor(private http: HttpClient, private auth: OpenIdConnectService, private appConfig: AppConfigProvider) {
    this.appConfig.environment.subscribe((env) => {
      this.environment = env.API;
      this.userTrackingDeleteUrl = `${this.environment.TRACKINGAPI}/api/deleteuser`; // Get deleted user from tracking
    });
  }

  public deleteUserTracking(user_id: number): Observable<any> {
    return this.http.post(this.userTrackingDeleteUrl, user_id).pipe(
      backoff(3, 250),
      catchError((err) => of([]))
    );
  }

  /**
   * Updates a single field from the EditTracking model
   * @param field Field to be updated
   * @param value Optional. If boolean, it will set the provided value to the field. If not, will toggle the previous value
   * @param debug Optional string that can be used for logging purposes
   */
  public updateModelDataChangesFlags(field: EditTrackingChangesKeys, value?: boolean, debug = '') {
    // Obtain a copy of the model
    const modelCopy: EditTrackingChanges = { ...this._modelDataHasChanges$.getValue() };
    // Assign boolean value or just toggle the previous if undefined
    modelCopy[field] = typeof value === 'boolean' ? value : !modelCopy[field];
    // Debug console log. Uncomment for debugging
    // console.log(`Debugging ${field} changed to ${value} for reason ${debug}`);
    // Push new model to Observer
    this.modelDataHasChanges$.next(modelCopy);
  }

  private handleUserTrackingForSaveButton(result, trackingType: TrackingResource) {
    const user_id = this.auth.userId;
    const filteredResults =
      result.filter((res) => {
        if (res && res.user_id) {
          return res.user_id === user_id;
        }
      }) || [];
    let field: EditTrackingChangesKeys;
    switch (trackingType) {
      case 'ExogenousLinesPeriodEquations':
        field = 'exogenousLines';
        break;
      case 'LiquiditySourcesPeriodEquations':
        field = 'liquiditySources';
        break;
      case 'AuxiliaryAccountsPeriodEquations':
        field = 'auxiliaryEquations';
        break;
    }

    this.updateModelDataChangesFlags(field, filteredResults.length > 0, 'handleUserTrackingForSaveButton');
  }

  /**
   * Iterates over all Edit tracking model to set all of them to false. Best used after a successful save.
   */
  public setAllModelDataChangesAfterSave() {
    Object.keys(this.modelDataHasChanges$.getValue()).forEach((key: EditTrackingChangesKeys) => {
      this.updateModelDataChangesFlags(key, false, `Set All To False. Iterating over ${key}`);
    });
  }

  public checkTempResults(): Observable<any> {
    const url = `${this.environment.TRACKINGAPI}/api/resultedit/checkTemp/`;
    return this.http.get(url).pipe(
      backoff(),
      catchError((err) => {
        console.error(`Problem retrieving resultset tracking data \nStacktrace: ${err}`);
        return err;
      }),
      tap((res) => {
        this.updateModelDataChangesFlags(
          'resultsets',
          res && res.length > 0,
          `checkTempResults ${Array.isArray(res) ? 'array' : typeof res} of size ${res.length || res}`
        );
      })
    );
  }
  public async getEditingUsers(resource, resourceId) {
    let resourcePath;

    switch (resource) {
      // These are not being used.
      // TODO: Add parameter typing, map the url, add Auxiliary Account Period Equations
      // case 'LiquiditySourcesPeriodEquations':
      //   resourcePath = `liqequations`;
      //   break;
      // case 'ExogenousLinesPeriodEquations':
      //   resourcePath = `exoequations`;
      //   break;
      case 'BalanceSheetStructure':
        resourcePath = `balancesheet`;
        break;
      default:
        resourcePath = null;
    }
    if (!resourcePath) {
      throw new Error('A wrong resources is being requested to TrackingService --> getEditingUsers()');
    }
    let url = `${this.environment.TRACKINGAPI}/api/tracking/editingusers/${resourcePath}`;
    if (resourceId) {
      url += `?resource_id=${resourceId}`;
    }

    const data = await this.http
      .get<string>(url)
      .toPromise()
      .catch((err) => {
        console.warn(err);
        return '';
      });

    return data;
  }
  public async getUserEditTrackingTable(resource: TrackingResource, resourceId: any = null) {
    let resourcePath;

    switch (resource) {
      case 'LiquiditySourcesPeriodEquations':
        resourcePath = `/tracking/liquiditysources/periodequations`;
        break;
      case 'ExogenousLinesPeriodEquations':
        resourcePath = `/tracking/exogenous/periodequations`;
        break;
      case 'BalanceSheetStructure':
        resourcePath = `/tracking/balancesheet`;
        break;
      case 'AuxiliaryAccountsPeriodEquations':
        resourcePath = `/tracking/balancesheet/periodequations`;
        break;
      default:
        resourcePath = null;
    }
    if (!resourcePath) {
      throw new Error('A wrong resources is being requested to TrackingService --> getEditingUsers()');
    }
    let url = `${this.environment.TRACKINGAPI}/api${resourcePath}/editingusers`;
    if (resourceId) {
      url += `?resource_id=${resourceId}`;
    }
    const data = await this.http
      .get(url)
      .pipe(tap((res) => this.handleUserTrackingForSaveButton(res, resource)))
      .toPromise()
      .catch((err) => null);
    return data;
  }

  /**
   * Signals the database to delete all errors from a temporary resultset
   * @param resultsetToUpdate The temp resultset that will have its errors cleared out
   */
  public ignoreErrorsForResultset(resultsetToUpdate: ResultsetItem) {
    const url = `${this.environment.TRACKINGAPI}/api/resultedit/ignore_errors`;

    const body: IgnoreError = {
      RunID: resultsetToUpdate.RunID,
      user_id: -1,
    };

    this.http.post(url, body).toPromise();
  }
}

type TrackingResource =
  | 'LiquiditySourcesPeriodEquations'
  | 'ExogenousLinesPeriodEquations'
  | 'BalanceSheetStructure'
  | 'AuxiliaryAccountsPeriodEquations';
